import { CloseOutlined } from '@icon';
import classNames from 'classnames';
import hoistNonReactStatistics from 'hoist-non-react-statics';
import RCDrawer, { DrawerProps as BaseProps } from 'rc-drawer';
import 'rc-drawer/assets/index.css';
import React from 'react';
import { useTheme } from 'styled-components';
import motionProps from '../drawers.motion';
import { DrawerStyles } from '../drawers.styled';
import { useDrawer } from '../drawers.utils';
import DrawerScroller from './components/drawer-scroller';
import {
  DrawerDescription,
  DrawerHeader,
  drawerScrollHeight,
  DrawerTitle,
  headerHeight,
  topOffset,
  virtualStyle,
} from './drawer.styled';

interface DrawerProps extends Omit<BaseProps, 'open'> {
  title?: React.ReactNode;
  visible?: boolean;
  closable?: boolean;
  footer?: React.ReactNode;
  isInfiniteScroll?: boolean;
  contentContainerStyle?: React.CSSProperties;
}

const Drawer = (
  {
    children,
    visible,
    width = 442,
    closable = true,
    title,
    className,
    maskClosable = true,
    mask = true,
    onClose,
    footer,
    contentContainerStyle,
    isInfiniteScroll,
    ...rest
  }: DrawerProps,
  ref?: React.Ref<HTMLDivElement>,
) => {
  const theme = useTheme();
  const hasHeader = closable || !!title;

  return (
    <>
      <DrawerStyles />
      <RCDrawer
        open={visible}
        width={width}
        {...rest}
        {...motionProps}
        rootClassName={classNames('ui-drawer', className, {
          'close-mask': maskClosable,
          'close-btn': closable,
        })}
        mask={mask}
        onClose={onClose}
      >
        {hasHeader ? (
          <DrawerHeader>
            <div className="ui-drawer-title">{title}</div>
            {closable ? (
              <button className="ui-drawer-close-btn" onClick={onClose}>
                <CloseOutlined color={theme.color.tint.primary} width={20} height={20} />
              </button>
            ) : null}
          </DrawerHeader>
        ) : null}
        <div
          className="ui-drawer-body"
          ref={ref}
          style={Object.assign({}, isInfiniteScroll ? virtualStyle : {}, contentContainerStyle)}
        >
          {children}
        </div>
        {footer ? <div className="ui-drawer-footer">{footer}</div> : null}
      </RCDrawer>
    </>
  );
};

Drawer.Title = DrawerTitle;
Drawer.Description = DrawerDescription;
Drawer.useDrawer = useDrawer;
Drawer.Scroller = DrawerScroller;

Drawer.headerHeight = headerHeight;
Drawer.topOffset = topOffset;
Drawer.drawerScrollHeight = drawerScrollHeight;

export default hoistNonReactStatistics(React.forwardRef(Drawer), Drawer);
